import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadPdfImages, PdfImage } from './pdfImages';

type Order = {
    id: number;
    tiendanube_id: string;
    contact_name: string;
    created_at: string;
    products: any;
    status: string;
    contact_phone?: string;
    discount?: number;
    discount_type?: string | null;   // 'fixed' = $; si no, %
    coupon_code?: string | null;
    coupon_amount?: number | null;
    total_price?: number;
    note?: string;
    owner_note?: string;
    source?: string;
    delivery_type?: string;
    delivery_address?: string;
    delivery_unit?: string | null;   // piso/depto aparte (migr 099)
    payment_status?: string;
    shipping_option?: string;
    deposit_amount?: number;
    shipping_cost_customer?: number;
    payment_details?: {
        method?: string;
        payment_method?: string;
        [key: string]: any;
    };
};

function getOrderDisplayId(order: { source?: string; tiendanube_id?: string; id: number }): string {
    if (order.source === 'woocommerce' && order.tiendanube_id) {
        const num = order.tiendanube_id.replace(/^(WC-|BT-)/i, '');
        return `BT-${num}`;
    }
    if ((order.source === 'tiendanube' || order.source === 'mercadolibre') && order.tiendanube_id) {
        return String(order.tiendanube_id);
    }
    return `K-${order.id}`;
}

// Brand Colors
const PRIMARY_COLOR = '#991b1b'; // Dark Red (Krilic Style)
const SECONDARY_COLOR = '#1f2937'; // Dark Gray
const TEXT_COLOR = '#374151'; // Gray 700
const LIGHT_BG = '#f3f4f6'; // Gray 100

// Logo teñido de un color (blanco para el encabezado bordó)
const tintedLogo = (url: string, color: string): Promise<string> => new Promise((resolve) => {
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => {
        const w = 500, h = Math.round(img.height * 500 / img.width);
        const c = document.createElement('canvas'); c.width = w; c.height = h;
        const ctx = c.getContext('2d'); if (!ctx) return resolve('');
        ctx.drawImage(img, 0, 0, w, h); ctx.globalCompositeOperation = 'source-in';
        ctx.fillStyle = color; ctx.fillRect(0, 0, w, h);
        resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve('');
    img.src = url;
});

const getBase64ImageFromURL = (url: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.onload = () => {
            const canvas = document.createElement('canvas');

            // Resize logic to avoid massive PDF files
            const MAX_WIDTH = 400; // sensible maximum width for a logo on a PDF
            let width = img.width;
            let height = img.height;

            if (width > MAX_WIDTH) {
                height = Math.round((height * MAX_WIDTH) / width);
                width = MAX_WIDTH;
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                // Better quality resizing
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/png', 0.8)); // 0.8 quality if it was jpeg, but it's png, so it just uses png encoding
            } else {
                reject(new Error('Canvas context is null'));
            }
        };
        img.onerror = () => {
            // Resolve with empty string to continue without logo if fails
            console.warn(`Failed to load image at ${url}`);
            resolve('');
        };
        img.src = url;
    });
};

// ═══════════════════ PDF de ORDEN / PRESUPUESTO — diseño de marca (15/10/2026) ═══════════════════
// Mismo cálculo de siempre (descuentos en cascada, envío sin descuento, seña, alternativas de pago):
// el TOTAL del PDF tiene que coincidir con total_price. Lo nuevo es solo la presentación:
// bordó Krilic, tipografía Manrope (public/fonts, OFL), foto de cada producto (Tiendanube) y
// datos del showroom (cost_settings). Si una foto o la fuente no cargan, el PDF sale igual.
const K = {
    brand: '#951F23',      // bordó del logo
    orange: '#F07A2E',     // acrílico naranja (Sun)
    blue: '#2F5BEA',       // acrílico azul eléctrico
    ink: '#1C1917',
    soft: '#57534E',
    faint: '#A8A29E',
    line: '#E6DCD2',
    cream: '#FBF6F0',      // fondo cálido de la página
    card: '#FFFFFF',
    blush: '#F5E6DF',
    ok: '#3F7D58',
};
const money = (n: number) => '$' + n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

let fontCache: Record<string, string> | null = null;
async function loadFonts(): Promise<Record<string, string> | null> {
    if (fontCache) return fontCache;
    try {
        const files = { regular: 'Manrope-Regular.ttf', semibold: 'Manrope-SemiBold.ttf', black: 'Manrope-ExtraBold.ttf' };
        const out: Record<string, string> = {};
        await Promise.all(Object.entries(files).map(async ([k, f]) => {
            const r = await fetch(`/fonts/${f}`);
            if (!r.ok) throw new Error(f);
            const buf = new Uint8Array(await r.arrayBuffer());
            let bin = ''; for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(buf.subarray(i, i + 0x8000)));
            out[k] = btoa(bin);
        }));
        fontCache = out;
        return out;
    } catch { return null; }
}

// Foto de cada producto: variante → producto de Tiendanube; si no, por nombre base.
async function productImages(items: any[]): Promise<Map<number, PdfImage>> {
    const out = new Map<number, PdfImage>();
    try {
        const { supabase } = await import('./supabase');
        const vids = items.map(i => Number(i?.tn_variant_id)).filter(Boolean);
        const pidByVariant = new Map<number, number>();
        if (vids.length) {
            const { data } = await supabase.from('tn_variants').select('id, product_id').in('id', vids);
            (data || []).forEach((v: any) => pidByVariant.set(Number(v.id), Number(v.product_id)));
        }
        const pids = Array.from(new Set([...Array.from(pidByVariant.values()), ...items.map(i => Number(i?.tn_product_id)).filter(Boolean)]));
        const bases = Array.from(new Set(items.map(i => String(i?.name || '').split(' (')[0].split(' – ')[0].trim()).filter(Boolean)));
        const [{ data: byId }, { data: byName }] = await Promise.all([
            pids.length ? supabase.from('tn_products').select('id, name, image_url').in('id', pids) : Promise.resolve({ data: [] as any[] }),
            bases.length ? supabase.from('tn_products').select('id, name, image_url').in('name', bases) : Promise.resolve({ data: [] as any[] }),
        ]);
        const urlById = new Map<number, string>(); const urlByName = new Map<string, string>();
        (byId || []).forEach((p: any) => p.image_url && urlById.set(Number(p.id), p.image_url));
        (byName || []).forEach((p: any) => p.image_url && urlByName.set(String(p.name).toLowerCase(), p.image_url));
        const urlFor = (it: any): string | undefined => {
            const pid = pidByVariant.get(Number(it?.tn_variant_id)) || Number(it?.tn_product_id) || 0;
            return urlById.get(pid) || urlByName.get(String(it?.name || '').split(' (')[0].split(' – ')[0].trim().toLowerCase());
        };
        const urls = items.map(urlFor);
        const imgs = await loadPdfImages(urls, 220);
        urls.forEach((u, i) => { if (u && imgs.has(u)) out.set(i, imgs.get(u)!); });
    } catch { /* sin fotos */ }
    return out;
}

async function showroomInfo(): Promise<{ address: string | null; hours: string | null }> {
    try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('cost_settings').select('local_address, local_hours').eq('id', 1).single();
        return { address: (data as any)?.local_address || null, hours: (data as any)?.local_hours || null };
    } catch { return { address: null, hours: null }; }
}

export const generateBudgetPDF = async (order: Order) => {
    const isBudget = order.status === 'presupuesto';
    const items: any[] = Array.isArray(order.products) ? order.products : [];
    const [logoUrl, fonts, imgs, showroom] = await Promise.all([
        tintedLogo('/logo.png', '#FFFFFF'), loadFonts(), productImages(items), showroomInfo(),
    ]);

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.width, H = doc.internal.pageSize.height, M = 16;
    let F = 'helvetica', FS = 'helvetica', FB = 'helvetica';
    let semiStyle: any = 'bold', blackStyle: any = 'bold';
    if (fonts) {
        doc.addFileToVFS('Manrope-Regular.ttf', fonts.regular); doc.addFont('Manrope-Regular.ttf', 'Manrope', 'normal');
        doc.addFileToVFS('Manrope-SemiBold.ttf', fonts.semibold); doc.addFont('Manrope-SemiBold.ttf', 'ManropeSemi', 'normal');
        doc.addFileToVFS('Manrope-ExtraBold.ttf', fonts.black); doc.addFont('Manrope-ExtraBold.ttf', 'ManropeBlack', 'normal');
        F = 'Manrope'; FS = 'ManropeSemi'; FB = 'ManropeBlack'; semiStyle = 'normal'; blackStyle = 'normal';
    }
    const font = (kind: 'r' | 's' | 'b', size: number, color: string) => {
        if (kind === 'r') doc.setFont(F, 'normal'); else if (kind === 's') doc.setFont(FS, semiStyle); else doc.setFont(FB, blackStyle);
        doc.setFontSize(size); doc.setTextColor(color);
    };

    // Acrílico translúcido: formas superpuestas con opacidad (como las lámparas)
    const alpha = (o: number) => { const G = (doc as any).GState; if (G) doc.setGState(new G({ opacity: o })); };
    const paintPage = () => { doc.setFillColor(K.cream); doc.rect(0, 0, W, H, 'F'); };
    paintPage();

    // ── Encabezado: bloque bordó con "sol" de acrílico ──
    const HB = 62;
    doc.setFillColor(K.brand); doc.rect(0, 0, W, HB, 'F');
    alpha(0.9); doc.setFillColor(K.orange); doc.circle(W - 22, 14, 30, 'F');
    alpha(0.55); doc.setFillColor('#FFB37A'); doc.circle(W - 48, 46, 15, 'F');
    alpha(0.35); doc.setFillColor(K.blue); doc.circle(W - 6, 54, 12, 'F');
    alpha(1);
    if (logoUrl) doc.addImage(logoUrl, 'PNG', M - 2, 11, 38, 0);
    else { font('b', 24, '#FFFFFF'); doc.text('krilic.', M, 24); }
    const fecha = new Date(order.created_at).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
    let numW = 0;
    if (isBudget) {
        // Presupuesto: que se lea a primera vista qué documento es (pedido 19/10/2026)
        font('b', 30, '#FFFFFF'); doc.text('Presupuesto', M, 48);
        font('r', 9.5, '#F3CFC9'); doc.text(`N° ${getOrderDisplayId(order)}  ·  ${fecha}`, M, 56);
    } else {
        font('s', 8, '#F3CFC9'); doc.setCharSpace(0.8); doc.text('TU PEDIDO', M, 40); doc.setCharSpace(0);
        font('b', 26, '#FFFFFF'); doc.text(`#${getOrderDisplayId(order)}`, M, 51);
        numW = doc.getTextWidth(`#${getOrderDisplayId(order)}`);
        font('r', 9, '#F3CFC9'); doc.text(fecha, M, 57.5);
    }
    if (!isBudget && order.payment_status === 'paid') {
        const t = 'PAGADO'; font('b', 8, K.brand); doc.setCharSpace(0.6);
        const tw = doc.getTextWidth(t) + 10;
        // al lado del número (no sobre los círculos)
        const px = M + numW + 5;
        doc.setFillColor('#FFFFFF'); doc.roundedRect(px, 45.2, tw, 7.5, 3.75, 3.75, 'F');
        doc.text(t, px + tw / 2, 50.3, { align: 'center' }); doc.setCharSpace(0);
    }

    // ── Saludo ──
    let y = HB + 14;
    const firstName = String(order.contact_name || '').trim().split(/\s+/)[0] || '';
    const hello = firstName && !/^showroom$/i.test(firstName) ? `¡Hola ${firstName.charAt(0).toUpperCase() + firstName.slice(1)}!` : '¡Hola!';
    font('b', 18, K.brand); doc.text(hello, M, y);
    font('r', 10, K.soft);
    const intro = isBudget
        ? 'Te armamos este presupuesto con todo lo que elegiste. Si querés cambiar algo, escribinos y lo ajustamos.'
        : order.payment_status === 'paid'
            ? 'Gracias por tu compra. Acá tenés el detalle de tu pedido: ya estamos con las manos en el acrílico.'
            : 'Acá tenés el detalle de tu pedido.';
    doc.text(doc.splitTextToSize(intro, W - 2 * M - 10), M, y + 7);
    y += 18;

    // ── Entrega (tarjeta) ──
    const envio = order.delivery_type === 'envio';
    const deliveryLines = envio
        ? doc.splitTextToSize(`${order.delivery_address || 'Dirección a confirmar'}${order.delivery_unit ? ` · Piso/Depto ${order.delivery_unit}` : ''}`, W - 2 * M - 70)
        : doc.splitTextToSize([showroom.address, showroom.hours].filter(Boolean).join(' · ') || 'Showroom Krilic', W - 2 * M - 70);
    const cardH = 14 + Math.min(3, deliveryLines.length) * 4.3;
    doc.setFillColor(K.card); doc.roundedRect(M, y, W - 2 * M, cardH, 4, 4, 'F');
    doc.setFillColor(envio ? K.orange : K.blue); doc.circle(M + 9, y + cardH / 2, 3, 'F');
    font('s', 7, K.faint); doc.setCharSpace(0.6); doc.text(envio ? 'ENVÍO A DOMICILIO' : 'RETIRO EN EL SHOWROOM', M + 17, y + 7); doc.setCharSpace(0);
    font('r', 9.5, K.ink); doc.text(deliveryLines.slice(0, 3), M + 17, y + 12.5);
    if (order.contact_phone) { font('r', 8.5, K.faint); doc.text(String(order.contact_phone), W - M - 6, y + 7, { align: 'right' }); }
    y += cardH + 10;

    // ── Lo que elegiste ──
    font('b', 12, K.ink); doc.text(items.length === 1 ? 'Lo que elegiste' : `Lo que elegiste (${items.length})`, M, y);
    y += 4;
    const hasImgs = imgs.size > 0;
    const body: any[] = [];
    let subtotal = 0;
    items.forEach((p: any) => {
        // Number(): Tiendanube manda precio y cantidad como texto ("226800.00")
        const price = Number(p.price) || 0, qty = Number(p.quantity) || 1, amount = price * qty;
        subtotal += amount;
        // sin etiquetas de marketing del catálogo ("[LANZAMIENTO]", "[EXCLUSIVO]") en un documento al cliente
        const name = String(p.name || '').replace(/^\s*(\[[^\]]*\]\s*)+/, '');
        body.push([hasImgs ? '' : null, name, `${qty} × ${money(price)}`, money(amount)].filter((_, i) => hasImgs || i !== 0));
    });
    const rowH = hasImgs ? 22 : 12;
    const off = hasImgs ? 1 : 0;
    const tints = ['#FCE3D3', '#E3E9FD', '#F5E6DF', '#FFF0D9'];
    // @ts-ignore
    autoTable(doc, {
        body, startY: y, theme: 'plain', margin: { left: M, right: M, bottom: 26, top: 16 },
        styles: { font: F, fontStyle: 'normal', fontSize: 9.5, textColor: K.ink, cellPadding: { top: 2, bottom: 2, left: 3, right: 3 }, minCellHeight: rowH, valign: 'middle', overflow: 'linebreak' },
        columnStyles: {
            ...(hasImgs ? { 0: { cellWidth: 26 } } : {}),
            [off + 0]: { cellWidth: 'auto', font: FS, fontStyle: semiStyle, fontSize: 10 },
            [off + 1]: { cellWidth: 40, halign: 'right', textColor: K.soft, fontSize: 9 },
            [off + 2]: { cellWidth: 34, halign: 'right', font: FB, fontStyle: blackStyle, fontSize: 10.5 },
        },
        willDrawPage: (d: any) => { if (d.pageNumber > 1) { paintPage(); doc.setFillColor(K.brand); doc.rect(0, 0, W, 6, 'F'); } },
        willDrawCell: (d: any) => {
            // cada producto en una tarjeta blanca redondeada
            if (d.section === 'body' && d.column.index === 0) {
                doc.setFillColor(K.card); doc.roundedRect(M, d.cell.y + 1, W - 2 * M, d.row.height - 2, 4, 4, 'F');
            }
        },
        didDrawCell: (d: any) => {
            if (d.section === 'body' && hasImgs && d.column.index === 0) {
                const img = imgs.get(d.row.index);
                const s = d.row.height - 7, bx = d.cell.x + 3, by = d.cell.y + 3.5;
                doc.setFillColor(tints[d.row.index % tints.length]); doc.roundedRect(bx, by, s, s, 3, 3, 'F');
                if (img) {
                    const r = Math.min((s - 2) / img.w, (s - 2) / img.h), w = img.w * r, h = img.h * r;
                    doc.addImage(img.dataUrl, 'JPEG', bx + (s - w) / 2, by + (s - h) / 2, w, h);
                }
            }
        },
    });
    // @ts-ignore
    let ty = doc.lastAutoTable.finalY + 8;

    // ── Totales (mismo cálculo que antes) ──
    const lines: Array<{ label: string; value: string; tone?: 'ok' | 'soft' }> = [];
    lines.push({ label: 'Subtotal', value: money(subtotal) });
    let total = subtotal;
    const isAbsolute = order.source === 'tiendanube' || order.source === 'woocommerce';
    if (isAbsolute) {
        // TN/WC: descuento absoluto único (consolidado en el webhook).
        if (order.discount && order.discount > 0) { total -= order.discount; lines.push({ label: 'Descuento', value: `−${money(order.discount)}`, tone: 'ok' }); }
    } else {
        // Manuales: cascada. Medio de pago sobre el subtotal (solo órdenes; en presupuestos va como
        // alternativa abajo), y el descuento extra sobre el resultado. % GUARDADO en la orden; fallback
        // órdenes viejas: 15 efectivo / 10 transferencia (con esos % se calcularon sus totales).
        if (!isBudget) {
            const pm = order.payment_details?.payment_method;
            const savedPct = order.payment_details?.pm_discount_pct;
            const pmPct = (savedPct === 0 || savedPct) ? Number(savedPct) : pm === 'efectivo' ? 15 : pm === 'transferencia' ? 10 : 0;
            if (pmPct > 0) {
                const a = (subtotal * pmPct) / 100; total -= a;
                lines.push({ label: `Descuento ${pm === 'efectivo' ? 'efectivo' : 'transferencia'} (${pmPct}%)`, value: `−${money(a)}`, tone: 'ok' });
            }
        }
        if (order.discount && order.discount > 0) {
            // discount_type 'fixed' (28/10/2026): monto en $; si no, % (como siempre)
            if (order.discount_type === 'fixed') {
                const a = Math.min(Number(order.discount), total); total -= a;
                lines.push({ label: 'Descuento', value: `−${money(a)}`, tone: 'ok' });
            } else {
                const a = (total * order.discount) / 100; total -= a;
                lines.push({ label: `Descuento (${order.discount}%)`, value: `−${money(a)}`, tone: 'ok' });
            }
        }
        // Cupón de Tiendanube en venta manual (monto ya calculado y guardado en la orden)
        if (!isBudget && Number(order.coupon_amount) > 0) {
            const a = Math.min(Number(order.coupon_amount), total); total -= a;
            lines.push({ label: `Cupón ${order.coupon_code || ''}`.trim(), value: `−${money(a)}`, tone: 'ok' });
        }
    }
    // Envío: se suma al total, NO recibe descuentos.
    const shipping = Number(order.shipping_cost_customer) || 0;
    const isFreeShipping = shipping === 0 && (order.delivery_type === 'envio' || /envio|domicilio/i.test(order.shipping_option || '')) && /gratis/i.test(order.shipping_option || '');
    if (isFreeShipping) lines.push({ label: 'Envío', value: 'Gratis', tone: 'ok' });
    if (shipping > 0) lines.push({ label: 'Envío', value: money(shipping) });
    const grandTotal = total + shipping;
    const deposit = Number(order.deposit_amount) || 0;
    const showDeposit = deposit > 0 && order.payment_status !== 'paid' && !isBudget;

    const panelW = 88, panelX = W - M - panelW;
    const panelH = 8 + lines.length * 6.6 + 4 + 16 + (showDeposit ? 15 : 0);
    const altH = isBudget ? 40 : 0;
    if (ty + Math.max(panelH, altH) > H - 28) { doc.addPage(); paintPage(); doc.setFillColor(K.brand); doc.rect(0, 0, W, 6, 'F'); ty = 20; }

    doc.setFillColor(K.card); doc.roundedRect(panelX, ty, panelW, panelH, 4, 4, 'F');
    let py = ty + 9;
    lines.forEach(l => {
        font('r', 9, K.soft); doc.text(l.label, panelX + 7, py);
        font(l.tone === 'ok' ? 's' : 'r', 9, l.tone === 'ok' ? K.ok : K.ink); doc.text(l.value, panelX + panelW - 7, py, { align: 'right' });
        py += 6.6;
    });
    // total en una píldora bordó (forma space age)
    const pillY = py - 1;
    doc.setFillColor(K.brand); doc.roundedRect(panelX + 4, pillY, panelW - 8, 13, 6.5, 6.5, 'F');
    font('s', 8, '#F3CFC9'); doc.setCharSpace(0.6); doc.text('TOTAL', panelX + 11, pillY + 8.3); doc.setCharSpace(0);
    font('b', 15, '#FFFFFF'); doc.text(money(grandTotal), panelX + panelW - 11, pillY + 8.9, { align: 'right' });
    py = pillY + 13 + 7;
    if (showDeposit) {
        font('r', 9, K.soft); doc.text('Seña recibida', panelX + 7, py);
        font('s', 9, K.ok); doc.text(`−${money(deposit)}`, panelX + panelW - 7, py, { align: 'right' });
        font('s', 10, K.ink); doc.text('Te queda abonar', panelX + 7, py + 6.5);
        font('b', 11, K.brand); doc.text(money(Math.max(0, grandTotal - deposit)), panelX + panelW - 7, py + 6.5, { align: 'right' });
    }

    // ── Izquierda: formas de pago (presupuesto) y nota ──
    const leftW = panelX - M - 8;
    let ly = ty;
    if (isBudget) {
        // Alternativas: el envío NO se descuenta. Efectivo y transferencia = 15% off (desde 2026-08-08).
        const cashTotal = total * 0.85 + shipping;
        const transferTotal = total * 0.85 + shipping;
        font('b', 11, K.ink); doc.text('¿Pagás en efectivo o transferencia?', M, ly + 5);
        font('r', 8.5, K.soft); doc.text('Tenés 15% off:', M, ly + 10.5);
        const cardW = (leftW - 4) / 2;
        [['Efectivo', cashTotal, K.orange], ['Transferencia', transferTotal, K.blue]].forEach(([label, v, c], i) => {
            const x = M + i * (cardW + 4), cy = ly + 14;
            doc.setFillColor(K.card); doc.roundedRect(x, cy, cardW, 22, 4, 4, 'F');
            doc.setFillColor(c as string); doc.circle(x + 6, cy + 7, 2, 'F');
            font('s', 8, K.soft); doc.text(label as string, x + 10, cy + 8);
            font('b', 12.5, K.ink); doc.text(money(v as number), x + 5, cy + 17);
        });
        ly += altH + 4;
    }
    if (order.note) {
        font('s', 7, K.faint); doc.setCharSpace(0.6); doc.text('NOTA', M, ly + 4); doc.setCharSpace(0);
        font('r', 9, K.soft);
        doc.text(doc.splitTextToSize(String(order.note), leftW).slice(0, 8), M, ly + 10);
    }

    // ── Pie: tagline de la marca ──
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        const fy = H - 20;
        alpha(0.9); doc.setFillColor(K.orange); doc.circle(M + 2.5, fy + 5.2, 2.5, 'F');
        alpha(0.6); doc.setFillColor(K.blue); doc.circle(M + 5.5, fy + 5.2, 2.5, 'F'); alpha(1);
        font('b', 10.5, K.brand); doc.text('Creamos deco que ilumina.', M + 11, fy + 6.6);
        font('r', 7.5, K.faint);
        doc.text(['krilic.com.ar', showroom.address ? `Showroom ${showroom.address}` : null].filter(Boolean).join('   ·   '), W - M, fy + 6.4, { align: 'right' });
        if (pages > 1) doc.text(`${i} / ${pages}`, W / 2, H - 6, { align: 'center' });
    }

    doc.save(`${isBudget ? 'Presupuesto' : 'Orden'}_Krilic_${getOrderDisplayId(order)}_${(order.contact_name || '').replace(/\s+/g, '_')}.pdf`);
    return doc.output('blob');
};

export const generateThermalLabelsPDF = async (orders: Order[]) => {
    // 100mm x 150mm in points (1mm = 2.83465 pts)
    const width = 100 * 2.83465;
    const height = 150 * 2.83465;

    const doc = new jsPDF({
        orientation: 'p',
        unit: 'pt',
        format: [width, height]
    });

    const logoUrl = await getBase64ImageFromURL('/logo.png');

    for (let i = 0; i < orders.length; i++) {
        const order = orders[i];
        if (i > 0) doc.addPage([width, height], 'p');

        const margin = 20;
        let currentY = 30;

        // Header: Logo and Order ID
        if (logoUrl) {
            doc.addImage(logoUrl, 'PNG', margin, 15, 60, 0);
        } else {
            doc.setFontSize(16);
            doc.setTextColor(PRIMARY_COLOR);
            doc.setFont('helvetica', 'bold');
            doc.text('KRILIC', margin, 35);
        }

        doc.setFontSize(14);
        doc.setTextColor(SECONDARY_COLOR);
        doc.setFont('helvetica', 'bold');
        const orderNum = getOrderDisplayId(order);
        doc.text(`#${orderNum}`, width - margin, 35, { align: 'right' });

        currentY = 65;
        doc.setDrawColor(LIGHT_BG);
        doc.setLineWidth(1);
        doc.line(margin, currentY, width - margin, currentY);
        currentY += 20;

        // Customer Info
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(SECONDARY_COLOR);
        doc.text(order.contact_name.toUpperCase(), margin, currentY);
        currentY += 15;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(TEXT_COLOR);
        if (order.contact_phone) {
            doc.text(order.contact_phone, margin, currentY);
            currentY += 12;
        }

        // Delivery Info
        const so = (order.delivery_type || '').toLowerCase();
        const isRetiro = so === 'retiro' || (order.shipping_option || '').toLowerCase().includes('retiro') || (order.shipping_option || '').toLowerCase().includes('local');

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(isRetiro ? '#2563eb' : '#9333ea'); // Blue for pickup, Purple for shipping
        const deliveryLabel = isRetiro ? 'RETIRO EN LOCAL' : 'ENVÍO A DOMICILIO';
        doc.text(deliveryLabel, margin, currentY);
        currentY += 12;

        if (!isRetiro && order.delivery_address) {
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(TEXT_COLOR);
            const addrLines = doc.splitTextToSize(
                order.delivery_unit ? `${order.delivery_address} — Piso/Depto ${order.delivery_unit}` : order.delivery_address,
                width - (margin * 2));
            doc.text(addrLines, margin, currentY);
            currentY += (addrLines.length * 12);
        }

        currentY += 10;
        doc.setLineDashPattern([2, 2], 0);
        doc.line(margin, currentY, width - margin, currentY);
        doc.setLineDashPattern([], 0);
        currentY += 15;

        // Products
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(SECONDARY_COLOR);
        doc.text('PRODUCTOS:', margin, currentY);
        currentY += 12;

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(TEXT_COLOR);
        if (Array.isArray(order.products)) {
            order.products.forEach((p: any) => {
                const pText = `${p.quantity}x ${p.name}`;
                const pLines = doc.splitTextToSize(pText, width - (margin * 2));

                // Check if we will overflow the page (roughly)
                if (currentY + (pLines.length * 12) > height - 40) {
                    doc.text('(...continúa)', margin, currentY);
                    return;
                }

                doc.text(pLines, margin, currentY);
                currentY += (pLines.length * 12) + 2;
            });
        }

        // Note
        if ((order.note || order.owner_note || '').replace(/origen de la visita.*$/is, '').trim()) {
            currentY += 5;
            const strip = (t?: string) => (t || '').replace(/origen de la visita.*$/is, '').trim();
            const note = [strip(order.note), strip(order.owner_note)].filter(Boolean).join(' · ');
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(9);
            doc.setTextColor('#6b7280');
            const noteLines = doc.splitTextToSize(`Nota: ${note}`, width - (margin * 2));
            if (currentY + (noteLines.length * 10) < height - 20) {
                doc.text(noteLines, margin, currentY);
            }
        }

        // Footer
        doc.setFontSize(8);
        doc.setTextColor('#9ca3af');
        const dateStr = new Date(order.created_at).toLocaleDateString('es-AR');
        doc.text(dateStr, width / 2, height - 10, { align: 'center' });
    }

    doc.save(`Etiquetas_Ordenes_${new Date().toISOString().split('T')[0]}.pdf`);
    return doc.output('blob');
};

// Productos cuyos pedidos requieren 2 etiquetas (vienen en 2 paquetes).
type ShippingType = 'RETIRA' | 'FLEX' | 'CORREO';

const getShippingType = (order: Order): ShippingType => {
    const so = (order.shipping_option || '').toLowerCase();
    // CORREO (Tiendanube Envío Nube) gana primero: el string puede contener "retiro" en
    // casos como "Punto de retiro (Envío Nube - Andreani a sucursal - ...)" y no es retira.
    if (so.includes('envio nube') || so.includes('envío nube')) return 'CORREO';
    // RETIRA: manual con retiro, o TN "Local (Local)" / "local"
    if (order.delivery_type === 'retiro') return 'RETIRA';
    if (so === 'local' || so === 'local (local)' || so.startsWith('local (') || so === 'retiras en local') return 'RETIRA';
    // FLEX: logística propia / envío gratis / flex
    if (so.includes('logistica propia') || so.includes('logística propia') || so.includes('envio gratis') || so.includes('envío gratis') || so.includes('flex')) return 'FLEX';
    // Manual con envío sin shipping_option → asumimos FLEX (logística propia)
    if (order.delivery_type === 'envio' && !order.shipping_option) return 'FLEX';
    return 'CORREO';
};

// Impresora térmica B&N: todos los badges usan caja negra con texto blanco bold.
// El diferenciador es la palabra, no el color.
const SHIPPING_LABELS: Record<ShippingType, string> = {
    RETIRA: 'RETIRA',
    FLEX:   'FLEX',
    CORREO: 'CORREO',
};

const MULTI_PACKAGE_KEYWORDS = ['sun&moon', 'sun & moon', 'mesa nido'];

const orderNeedsTwoLabels = (order: Order): boolean => {
    if (!Array.isArray(order.products)) return false;
    return order.products.some((p: any) => {
        const name = (p.name || '').toLowerCase();
        return MULTI_PACKAGE_KEYWORDS.some(k => name.includes(k));
    });
};

type CompactLabelEntry = { order: Order; part?: '1/2' | '2/2' };

const expandOrdersForCompactLabels = (orders: Order[]): CompactLabelEntry[] => {
    const out: CompactLabelEntry[] = [];
    for (const o of orders) {
        if (orderNeedsTwoLabels(o)) {
            out.push({ order: o, part: '1/2' });
            out.push({ order: o, part: '2/2' });
        } else {
            out.push({ order: o });
        }
    }
    return out;
};

interface CompactPlacement {
    entry: CompactLabelEntry;
    x: number;
    y: number;
    w: number;
    h: number;
    isCompact: boolean; // fuente reducida cuando ocupa el slot mínimo en perPage=4
    itemStart?: number; // si la orden se paginó, índice inicial de items a dibujar
    itemEnd?: number;   // si la orden se paginó, índice final (exclusive)
    chunkInfo?: { idx: number; total: number }; // "Hoja 1/3" en órdenes paginadas
}

const compactFontConfig = (isCompact: boolean) => ({
    margin: isCompact ? 6 : 8,
    orderFont: isCompact ? 17 : 21,
    nameFont: isCompact ? 11 : 12,
    itemFont: isCompact ? 10 : 11,
    itemLineHeight: isCompact ? 12 : 13,
    checkboxSize: isCompact ? 8 : 10,
    badgeFont: isCompact ? 15 : 19,
    badgeRadius: isCompact ? 12 : 16,
    nameGap: isCompact ? 13 : 17,
    dividerGap: isCompact ? 11 : 14,
});

// Etiqueta compacta: 100mm x 150mm portrait, layout vertical apilado.
// perPage es la densidad ideal (2 o 4), pero las órdenes con muchos items se promueven
// automáticamente al doble de altura, o a la hoja entera, para que TODOS los items quepan
// sin truncar. Si una orden tiene productos en MULTI_PACKAGE_KEYWORDS se imprimen 2 etiquetas
// idénticas marcadas "1/2" y "2/2" con un círculo rojo arriba a la derecha.
export const generateCompactThermalLabelsPDF = async (orders: Order[], perPage: 2 | 4 = 2) => {
    const MM = 2.83465;
    const width = 100 * MM;
    const height = 150 * MM;

    const doc = new jsPDF({
        orientation: 'p',
        unit: 'pt',
        format: [width, height],
    });

    const labels = expandOrdersForCompactLabels(orders);
    const isCompactMode = perPage === 4;

    // jsPDF.splitTextToSize no rompe palabras sin espacios. Si una "palabra" es más ancha
    // que el ancho disponible (ej. un SKU largo "BLOOM-NEGRO-MATE-2X100W"), la deja en
    // una sola línea y se va del papel. Este helper la parte por caracteres en ese caso.
    const wrapText = (text: string, maxWidth: number): string[] => {
        const initial: string[] = doc.splitTextToSize(text, maxWidth);
        const out: string[] = [];
        for (const line of initial) {
            if (doc.getTextWidth(line) <= maxWidth) {
                out.push(line);
                continue;
            }
            let buf = '';
            for (const ch of line) {
                const test = buf + ch;
                if (buf && doc.getTextWidth(test) > maxWidth) {
                    out.push(buf);
                    buf = ch;
                } else {
                    buf = test;
                }
            }
            if (buf) out.push(buf);
        }
        return out;
    };

    // Notas del pedido en la etiqueta del taller (02/11/2026): "cable de 2,5 m", "transparentes de
    // 110 cm"… La nota del cliente y la interna, sin el rastro de UTM que agrega Tiendanube.
    const orderNotes = (order: Order): string[] => {
        const clean = (t?: string) => (t || '').split('\n')
            .filter(l => !/^\s*(origen de la visita|utm_|fbclid|gclid)/i.test(l)).join('\n')
            .replace(/origen de la visita.*$/is, '').trim();
        return [clean(order.note) && `Cliente: ${clean(order.note)}`, clean(order.owner_note) && `Interna: ${clean(order.owner_note)}`].filter(Boolean) as string[];
    };
    const noteLinesOf = (order: Order, cfg: any, innerW: number): string[] => {
        const notes = orderNotes(order);
        if (!notes.length) return [];
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(cfg.itemFont);
        return notes.flatMap(n => wrapText(n.replace(/\s*\n\s*/g, ' · '), innerW - 8));
    };
    const measureNotes = (order: Order, cfg: any, innerW: number): number => {
        const lines = noteLinesOf(order, cfg, innerW);
        return lines.length ? 6 + cfg.itemFont + lines.length * cfg.itemLineHeight + 8 : 0;
    };

    const measureItemHeight = (item: any, cfg: any, itemInnerW: number): number => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(cfg.itemFont);
        const text = `${item.quantity}× ${item.name}`;
        const lines = wrapText(text, itemInnerW);
        return lines.length * cfg.itemLineHeight + 2;
    };

    // Altura del header (order#, badge envío, nombre, divider) sin contar items
    const measureHeaderHeight = (order: Order, cfg: any): number => {
        let h = cfg.margin + cfg.orderFont * 0.85;
        if ((order.contact_name || '').trim()) {
            h += cfg.nameGap + cfg.nameFont + 1;
        } else {
            h += cfg.nameGap;
        }
        h += cfg.dividerGap;
        return h;
    };

    const measureOrderHeight = (order: Order, isCompact: boolean, itemStart = 0, itemEnd?: number): number => {
        const cfg = compactFontConfig(isCompact);
        const innerW = width - cfg.margin * 2;
        const itemInnerW = innerW - cfg.checkboxSize - 5;
        let h = measureHeaderHeight(order, cfg);
        const items = Array.isArray(order.products) ? order.products as any[] : [];
        const end = itemEnd ?? items.length;
        if (items.length > 0 && end > itemStart) {
            for (let i = itemStart; i < end; i++) {
                h += measureItemHeight(items[i], cfg, itemInnerW);
            }
        } else if (items.length === 0) {
            h += cfg.itemFont + 4;
        }
        if (itemEnd == null || itemEnd >= items.length) h += measureNotes(order, cfg, innerW);
        return h + cfg.margin;
    };

    const drawZone = (p: CompactPlacement) => {
        const cfg = compactFontConfig(p.isCompact);
        const { order, part } = p.entry;
        const innerW = p.w - cfg.margin * 2;

        // Badge envío (esquina superior derecha). Caja negra rellena con texto blanco bold,
        // pensado para impresora térmica B&N. Si la orden es multi-paquete (1/2 2/2),
        // el badge del envío va arriba y el part badge debajo.
        const ship = getShippingType(order);
        const shipLabel = SHIPPING_LABELS[ship];
        const shipPadX = p.isCompact ? 4 : 6;
        const shipPadY = p.isCompact ? 2 : 3;
        const shipFont = p.isCompact ? 9 : 11;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(shipFont);
        const shipW = doc.getTextWidth(shipLabel) + shipPadX * 2;
        const shipH = shipFont + shipPadY * 2;
        const shipX = p.x + p.w - cfg.margin - shipW;
        const shipY = p.y + cfg.margin;
        doc.setFillColor('#000000');
        doc.rect(shipX, shipY, shipW, shipH, 'F');
        doc.setTextColor('#ffffff');
        doc.text(shipLabel, shipX + shipW / 2, shipY + shipH / 2 + shipFont / 3, { align: 'center' });

        if (part) {
            const cx = p.x + p.w - cfg.margin - cfg.badgeRadius;
            const cy = shipY + shipH + cfg.badgeRadius + 4;
            doc.setDrawColor(PRIMARY_COLOR);
            doc.setLineWidth(1.5);
            doc.circle(cx, cy, cfg.badgeRadius, 'S');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(cfg.badgeFont);
            doc.setTextColor(PRIMARY_COLOR);
            doc.text(part, cx, cy + cfg.badgeFont / 3, { align: 'center' });
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(cfg.orderFont);
        doc.setTextColor(SECONDARY_COLOR);
        const orderY = p.y + cfg.margin + cfg.orderFont * 0.85;
        const orderLabel = `#${getOrderDisplayId(order)}${p.chunkInfo ? ` (${p.chunkInfo.idx}/${p.chunkInfo.total})` : ''}`;
        doc.text(orderLabel, p.x + cfg.margin, orderY);

        const name = (order.contact_name || '').trim();
        let y = orderY + cfg.nameGap;
        if (name) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(cfg.nameFont);
            doc.setTextColor(TEXT_COLOR);
            const nameLine = wrapText(name, innerW)[0];
            doc.text(nameLine, p.x + cfg.margin, y);
            y += cfg.nameFont + 1;
        }

        doc.setDrawColor(LIGHT_BG);
        doc.setLineWidth(0.8);
        doc.line(p.x + cfg.margin, y, p.x + p.w - cfg.margin, y);
        y += cfg.dividerGap;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(cfg.itemFont);
        doc.setTextColor(TEXT_COLOR);
        const checkboxX = p.x + cfg.margin;
        const textX = checkboxX + cfg.checkboxSize + 5;
        const itemInnerW = innerW - cfg.checkboxSize - 5;

        const items = Array.isArray(order.products) ? order.products as any[] : [];
        const start = p.itemStart ?? 0;
        const end = p.itemEnd ?? items.length;

        if (items.length > 0 && end > start) {
            for (let i = start; i < end; i++) {
                const it = items[i];
                const text = `${it.quantity}× ${it.name}`;
                const lines = wrapText(text, itemInnerW);
                doc.setDrawColor(SECONDARY_COLOR);
                doc.setLineWidth(0.6);
                doc.rect(checkboxX, y - cfg.checkboxSize + 1, cfg.checkboxSize, cfg.checkboxSize, 'S');
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(TEXT_COLOR);
                doc.text(lines, textX, y);
                y += lines.length * cfg.itemLineHeight + 2;
            }
        } else if (items.length === 0) {
            doc.setFont('helvetica', 'italic');
            doc.setTextColor('#9ca3af');
            doc.text('(sin items)', p.x + cfg.margin, y);
        }

        // Notas (solo en la última parte del pedido): recuadro con "NOTA" para que el taller la vea
        if (end >= items.length) {
            const nLines = noteLinesOf(order, cfg, innerW);
            if (nLines.length) {
                y += 2;
                const boxH = 4 + cfg.itemFont + nLines.length * cfg.itemLineHeight + 4;
                doc.setDrawColor('#000000');
                doc.setLineWidth(1.2);
                doc.rect(p.x + cfg.margin, y - cfg.itemFont + 2, innerW, boxH, 'S');
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(cfg.itemFont - 1);
                doc.setTextColor('#000000');
                doc.text('NOTA', p.x + cfg.margin + 4, y + 2);
                y += cfg.itemFont + 2;
                doc.setFontSize(cfg.itemFont);
                doc.text(nLines, p.x + cfg.margin + 4, y + 2);
                y += nLines.length * cfg.itemLineHeight + 4;
            }
        }

        // Footer "continúa..." si hay más items en otra hoja
        if (p.chunkInfo && p.chunkInfo.idx < p.chunkInfo.total) {
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(p.isCompact ? 8 : 9);
            doc.setTextColor('#9ca3af');
            doc.text('continúa...', p.x + p.w - cfg.margin, y + cfg.margin / 2, { align: 'right' });
        }
    };

    // Packing continuo por altura real. Cada orden ocupa el alto que necesita y se apilan
    // verticalmente. Cuando la próxima no entra, se abre página nueva.
    // perPage define los tamaños de fuente: 2 = grandes, 4 = compactos (más densidad por hoja).
    // Si una sola orden no entra ni achicada, se parte por ítems en múltiples hojas.
    const pages: CompactPlacement[][] = [];
    let current: CompactPlacement[] = [];
    let yCursor = 0;

    for (const entry of labels) {
        let useCompact = isCompactMode;
        let needed = measureOrderHeight(entry.order, useCompact);

        // Si no entra en lo que queda, cierro página y abro otra
        if (yCursor + needed > height && current.length > 0) {
            pages.push(current);
            current = [];
            yCursor = 0;
        }
        // Si una orden sola no entra ni en una hoja vacía, achico a fonts compactos
        if (needed > height && !useCompact) {
            useCompact = true;
            needed = measureOrderHeight(entry.order, useCompact);
        }

        if (needed <= height) {
            current.push({ entry, x: 0, y: yCursor, w: width, h: needed, isCompact: useCompact });
            yCursor += needed;
        } else {
            // Orden gigante: ni siquiera en compacto entra en una hoja → paginar por ítems
            if (current.length > 0) {
                pages.push(current);
                current = [];
                yCursor = 0;
            }
            const cfg = compactFontConfig(useCompact);
            const innerW = width - cfg.margin * 2;
            const itemInnerW = innerW - cfg.checkboxSize - 5;
            const headerH = measureHeaderHeight(entry.order, cfg);
            const footerH = cfg.margin;
            const items = Array.isArray(entry.order.products) ? entry.order.products as any[] : [];
            const chunks: { start: number; end: number; height: number }[] = [];
            let i = 0;
            while (i < items.length) {
                const start = i;
                let chunkH = headerH;
                while (i < items.length) {
                    const itH = measureItemHeight(items[i], cfg, itemInnerW);
                    if (chunkH + itH + footerH > height) break;
                    chunkH += itH;
                    i++;
                }
                if (start === i) {
                    // Item solo más grande que la hoja: forzar 1 ítem y aceptar overflow
                    chunkH += measureItemHeight(items[i], cfg, itemInnerW);
                    i++;
                }
                chunks.push({ start, end: i, height: chunkH + footerH });
            }
            for (let c = 0; c < chunks.length; c++) {
                const ch = chunks[c];
                pages.push([{
                    entry, x: 0, y: 0, w: width, h: ch.height,
                    isCompact: useCompact,
                    itemStart: ch.start, itemEnd: ch.end,
                    chunkInfo: chunks.length > 1 ? { idx: c + 1, total: chunks.length } : undefined,
                }]);
            }
        }
    }
    if (current.length > 0) pages.push(current);

    // Render
    let firstPage = true;
    for (const pagePlacements of pages) {
        if (!firstPage) doc.addPage([width, height], 'p');
        firstPage = false;

        // Dividers entre etiquetas (líneas horizontales en cada borde inferior, salvo el último)
        doc.setDrawColor(LIGHT_BG);
        doc.setLineDashPattern([3, 3], 0);
        doc.setLineWidth(0.5);
        for (let i = 0; i < pagePlacements.length - 1; i++) {
            const p = pagePlacements[i];
            const yLine = p.y + p.h;
            doc.line(8, yLine, width - 8, yLine);
        }
        doc.setLineDashPattern([], 0);

        for (const p of pagePlacements) drawZone(p);
    }

    doc.save(`Etiquetas_Compactas_${perPage === 4 ? 'denso' : 'normal'}_${new Date().toISOString().split('T')[0]}.pdf`);
    return doc.output('blob');
};
